export const MLRO_CASE_STATUSES = [
    'open',
    'in_review',
    'escalated',
    'closed_no_action',
    'closed_sar_filed',
    'closed_str_filed',
] as const
export type MlroCaseStatus = (typeof MLRO_CASE_STATUSES)[number]

export const MLRO_CASE_STATUS_LABELS: Record<MlroCaseStatus, string> = {
    open: 'Open',
    in_review: 'In review',
    escalated: 'Escalated',
    closed_no_action: 'Closed — no action',
    closed_sar_filed: 'Closed — SAR filed',
    closed_str_filed: 'Closed — STR filed',
}

// Statuses that demand a closing note to be supplied with the transition.
export const MLRO_CASE_CLOSING_STATUSES: MlroCaseStatus[] = [
    'closed_no_action',
    'closed_sar_filed',
    'closed_str_filed',
]

export function isClosingStatus(status: MlroCaseStatus): boolean {
    return MLRO_CASE_CLOSING_STATUSES.includes(status)
}

// `mlrocases.Severity` is the alert ladder (`info`, `warn`, `critical`); the console says `warning`
// for `warn`, as the alerts list does. `mlro-wire.ts` maps between them.
export const MLRO_CASE_SEVERITIES = ['info', 'warning', 'critical'] as const
export type MlroCaseSeverity = (typeof MLRO_CASE_SEVERITIES)[number]

// Subject types currently emitted by the service. Open union so future
// categories don't break the wire decoder.
export const MLRO_CASE_SUBJECT_TYPES = [
    'player',
    'alert',
    'wallet',
    'system',
    'sybil_cluster',
] as const
export type MlroCaseSubjectType = (typeof MLRO_CASE_SUBJECT_TYPES)[number] | (string & {})

export const MLRO_CASE_SUBJECT_LABELS: Record<string, string> = {
    player: 'Player',
    alert: 'Alert',
    wallet: 'Wallet',
    system: 'System',
    sybil_cluster: 'Sybil cluster',
}

// A case as the console renders it, mapped from `caseWire`. The list endpoint omits notes and
// alerts; the detail endpoint adds them. Operators are identified by wallet, not user id.
export interface MlroCaseSummary {
    caseId: string
    openedAt: string
    // Null when the service auto-opened the case.
    openedByAddress: string | null
    title: string
    severity: MlroCaseSeverity
    status: MlroCaseStatus
    subjectType: MlroCaseSubjectType
    subjectId: string
    summary: string
    closedAt: string | null
    closedByAddress: string | null
    closingNote: string | null
}

export interface MlroCaseNote {
    noteId: string
    writtenAt: string
    writtenByAddress: string
    body: string
    attachments: MlroCaseAttachment[]
}

export interface MlroCaseAttachment {
    name: string
    url?: string
    contentType?: string
}

// The alert's own record, when the service could hydrate it.
export interface MlroCaseAttachedAlertDetail {
    category: MlroAlertCategory
    severity: MlroAlertSeverity
    summary: string
    raisedAt: string
}

export interface MlroCaseAttachedAlert {
    alertId: string
    attachedAt: string
    // Null when the handler could not load the alert row and omitted `alert`.
    alert: MlroCaseAttachedAlertDetail | null
}

export interface MlroCase extends MlroCaseSummary {
    notes: MlroCaseNote[]
    attachedAlerts: MlroCaseAttachedAlert[]
}

export interface MlroCaseListFilters {
    status?: MlroCaseStatus | ''
    severity?: MlroCaseSeverity | ''
    subjectType?: MlroCaseSubjectType | ''
    subjectId?: string
}

export interface MlroCaseListResponse {
    cases: MlroCaseSummary[]
    nextCursor: string | null
}

export interface CreateMlroCaseRequest {
    title: string
    severity: MlroCaseSeverity
    subjectType: MlroCaseSubjectType
    subjectId: string
    summary: string
    attachAlertIds?: string[]
}

// `createCaseResponse` mapped by `postMlroCase`.
export interface CreateMlroCaseResponse {
    caseId: string
    failedAttachments: string[]
}

// blockpot-service `createCaseRequest`: snake_case keys.
export interface CreateMlroCaseWireRequest {
    title: string
    severity: MlroSeverityWire
    subject_type: MlroCaseSubjectType
    subject_id: string
    summary: string
    attach_alert_ids?: string[]
}

// blockpot-service `createCaseResponse`, sent with 201. `failed_attachments` is omitempty and lists
// the alert ids the case was opened without.
export interface CreateMlroCaseWireResponse {
    case_id: string
    failed_attachments?: string[]
}

export interface MlroCaseNoteRequest {
    body: string
    attachments?: MlroCaseAttachment[]
}

export interface MlroCaseNoteResponse {
    noteId: string
}

export interface MlroCaseStatusRequest {
    status: MlroCaseStatus
    closingNote?: string
}

export type MlroCaseExportFormat = 'sar' | 'str'

// Categories the service emits. Open union so a new
// category doesn't crash the UI; just falls back to the raw string.
export const MLRO_ALERT_CATEGORIES = [
    'sanctions_oracle_list_disagreement',
    'registry_sync_failure',
    'webhook_signature_fail',
    'sybil_threshold_exceeded',
    'kyc_grace_expired',
    'manual',
] as const
export type MlroAlertCategory = (typeof MLRO_ALERT_CATEGORIES)[number] | (string & {})

export const MLRO_ALERT_CATEGORY_LABELS: Record<string, string> = {
    sanctions_oracle_list_disagreement: 'Sanctions oracle disagreement',
    registry_sync_failure: 'Registry sync failure',
    webhook_signature_fail: 'Webhook signature failure',
    sybil_threshold_exceeded: 'Sybil threshold exceeded',
    kyc_grace_expired: 'KYC grace expired',
    manual: 'Manual',
}

export const MLRO_ALERT_SEVERITIES = ['info', 'warning', 'critical'] as const
export type MlroAlertSeverity = (typeof MLRO_ALERT_SEVERITIES)[number]

export interface MlroAlertSummary {
    alertId: string
    raisedAt: string
    category: MlroAlertCategory
    severity: MlroAlertSeverity
    subjectType: MlroCaseSubjectType
    subjectId: string
    summary: string
    acknowledgedAt: string | null
    acknowledgedBy: string | null
    caseId: string | null
}

export interface MlroAlertListFilters {
    category?: MlroAlertCategory | ''
    severity?: MlroAlertSeverity | ''
    acknowledged?: 'true' | 'false' | ''
}

export interface MlroAlertListResponse {
    alerts: MlroAlertSummary[]
    nextCursor: string | null
}

export interface MlroDashboardSummary {
    openAlerts: number
    openCases: number
    escalatedCases: number
    refreshedAt: string
}

// --- blockpot-service wire shapes (BLO-946) -------------------------------------------------
// `internal/httpapi/mlro_cases_handler.go` and `internal/mlro/query.go` on origin/dev, exactly as
// they serialise: snake_case keys, `omitempty` fields optional. `src/lib/mlro-wire.ts` maps them.

// `mlrocases.Severity` and the `mlro_alerts.severity` column. The console says `warning`.
export type MlroSeverityWire = 'info' | 'warn' | 'critical'

// `caseWire`. `opened_by` and `closed_by` are checksummed operator wallets; an auto-opened case
// has no `opened_by`.
export interface MlroCaseWire {
    case_id: string
    opened_at: string
    opened_by?: string
    title: string
    severity: MlroSeverityWire
    status: MlroCaseStatus
    subject_type: string
    subject_id: string
    summary: string
    closed_at?: string
    closed_by?: string
    closing_note?: string
}

// `mlrocases.Attachment`.
export interface MlroCaseAttachmentWire {
    filename: string
    url?: string
    mime?: string
}

// `noteWire`. `written_by` is the operator wallet.
export interface MlroCaseNoteWire {
    note_id: string
    written_at: string
    written_by: string
    body: string
    attachments: MlroCaseAttachmentWire[]
}

// `mlro.Record`, as the case detail hydrates an attached alert.
export interface MlroAlertRecordWire {
    alert_id: string
    fired_at: string
    severity: MlroSeverityWire
    category: string
    dedup_key: string
    summary: string
    details: Record<string, unknown>
    acknowledged_at?: string
    acknowledged_by?: string
    acknowledged_by_address?: string
    resolved_at?: string
}

// `alertLinkWire`. `alert` is omitted when the alert row cannot be loaded.
export interface MlroCaseAlertLinkWire {
    alert_id: string
    attached_at: string
    alert?: MlroAlertRecordWire
}

// `casesListResponse`. `next_cursor` is not omitempty: it is `""` on the last page.
export interface MlroCaseListWire {
    cases: MlroCaseWire[]
    next_cursor: string
}

// `caseDetailResponse`.
export interface MlroCaseDetailWire {
    case: MlroCaseWire
    notes: MlroCaseNoteWire[]
    alerts: MlroCaseAlertLinkWire[]
}

// `addNoteRequest` / `addNoteResponse`.
export interface MlroCaseNoteWireRequest {
    body: string
    attachments?: MlroCaseAttachmentWire[]
}
export interface MlroCaseNoteWireResponse {
    note_id: string
}

// `setStatusRequest`. The response is the updated `caseWire`.
export interface MlroCaseStatusWireRequest {
    status: MlroCaseStatus
    closing_note?: string
}

// `attachAlertRequest`, shared by attach-alert and detach-alert. Both answer 204.
export interface MlroCaseAlertWireRequest {
    alert_id: string
}

// `alertSummaryJSON` from `internal/httpapi/mlro_alerts_handler.go`. Unlike the cases handler,
// the alerts list already speaks camelCase (BLO-729): the service maps `warn` to `warning` and
// derives `subjectType` / `subjectId` from the dedup key and `caseId` from `mlro_case_alerts`.
export interface MlroAlertSummaryWire {
    alertId: string
    raisedAt: string
    category: string
    severity: 'info' | 'warning' | 'critical'
    subjectType: string
    subjectId: string
    summary: string
    acknowledgedAt: string | null
    acknowledgedBy: string | null
    caseId: string | null
}

// The alerts list response. The handler always sends `nextCursor: null`; it does not paginate.
export interface MlroAlertListWire {
    alerts: MlroAlertSummaryWire[]
    nextCursor: null
}
